import { ChangeDetectionStrategy, Component, Input, input, OnInit } from '@angular/core';
import { ComponentStatus, StateComponent } from '../../../../../../../components/state/state.component';
import { IFormBettings, IFormConfig, IGroupStageBetting, IGroupStageFormConfig, ITeamLookupDto, ITeamsMetadata, UnderOver } from '@oogaday/shared';
import { UntypedFormGroup } from '@angular/forms';
import { IBettingOption, IDropdownConfig } from '../common/model/betting.model';
import { UtilsService } from '../../../../../../../services/utils.service';
import { OptionsProviderService } from '../common/services/options-provider.service';
import { memoize } from 'utils-decorators';
import { ButtonGroupBettingComponent } from '../common/components/button-group-betting/button-group-betting.component';
import { DropdownsBettingComponent } from '../common/components/dropdowns-betting/dropdowns-betting.component';
import { InOrderQualifiresComponent } from './in-order-qualifires/in-order-qualifires.component';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';


@Component({
  selector: 'oo-group-stage-form-filler',
  templateUrl: './group-stage-form-filler.component.html',
  styleUrls: ['./group-stage-form-filler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, InOrderQualifiresComponent, DropdownsBettingComponent, ButtonGroupBettingComponent],
})
export class GroupStageFormFillerComponent extends StateComponent<GroupStageFormFillerComponentState> implements OnInit {


  readonly metadata = input.required<ITeamsMetadata>();
  state: GroupStageFormFillerComponentState = {
    status: 'ready',
    config: null!,
    betting: undefined,
    bettingForm: null!,
    groups: undefined!,
    isSubmissionMode: false,
    qualifiersOptions: [],
    totalGoalsOptions: [],
    mostScoringTeamOptions: [],
    leastScoringTeamOptions: [],
    worstTeamOptions: [],
    groupWithMostScoredGoalsOptions: undefined!,
    groupWithLeastScoredGoalsOptions: undefined!,
    extraQualifiersOptions: undefined!,
  };


  @Input() set config(config: IFormConfig) {
    this.updateState({ config: config as IGroupStageFormConfig });
  }

  @Input() set bettingForm(bettingForm: UntypedFormGroup) {
    this.updateState({ bettingForm });
  }

  @Input() set betting(betting: IFormBettings<any> | undefined) {
    this.updateState({ betting });
  }

  @Input() set isSubmissionMode(isSubmissionMode: boolean) {
    this.updateState({ isSubmissionMode });
  }

  ngOnInit(): void {
    this.setGroups();

    this.updateState({
      qualifiersOptions: this.optionsPerGroup(this.state.groups),
      totalGoalsOptions: OptionsProviderService.getUnderOverOptions(this.getNumOfGoalsInStage()),
      mostScoringTeamOptions: OptionsProviderService.getTeamsOptions('Select a team', this.groupsToTeams(this.state.groups)),
      leastScoringTeamOptions: OptionsProviderService.getTeamsOptions('Select a team', this.groupsToTeams(this.state.groups)),
      worstTeamOptions: OptionsProviderService.getTeamsOptions('Select a team', this.groupsToTeams(this.state.groups)),
      groupWithMostScoredGoalsOptions: this.getGroupOptions('Select a group', this.state.groups),
      groupWithLeastScoredGoalsOptions: this.getGroupOptions('Select a group', this.state.groups),
      extraQualifiersOptions: OptionsProviderService.getTeamsOptions('Select a team', this.groupsToTeams(this.state.groups)),
    });
  }

  private setGroups(): void {
    const teamsMap = UtilsService.listToMap(this.metadata().teams, 'id');

    this.updateState({
      groups: this.state.config.groups
        .map((group) => group.map(teamId => teamsMap.get(teamId)!)),
    });
  }

  private getGroupOptions(label: string, groups: ITeamLookupDto[][]): IDropdownConfig[] {
    return [
      {
        label,
        options: groups.map((group, i) => ({
          name: `Group ${UtilsService.numToLetter(i)}`,
          value: i,
        })),
      },
    ];
  }

  private optionsPerGroup(groups: ITeamLookupDto[][]): IDropdownConfig[] {
    return groups
      .map((group, i) => ({
          label: `Group ${UtilsService.numToLetter(i)}`,
          options: group.map((team) => ({
            value: team.id,
            name: team.name,
          })),
        }),
      );
  }

  private getNumOfGoalsInStage(): number {
    const numOfMatchesInGroupInRound = (this.state.groups[0].length - 1) * 2;

    return Math.floor(this.state.groups.length * numOfMatchesInGroupInRound * this.state.config.numOfRounds * 2.5);
  }

  @memoize<any>()
  private groupsToTeams(groups: ITeamLookupDto[][]): ITeamLookupDto[] {
    return groups.flatMap((group) => group)
      .sort((a, b) => a.name < b.name ? -1 : 1);
  }
}

interface GroupStageFormFillerComponentState {
  status: ComponentStatus;
  config: IGroupStageFormConfig;
  betting: IGroupStageBetting | undefined;
  groups: ITeamLookupDto[][];
  bettingForm: UntypedFormGroup;
  isSubmissionMode: boolean;
  qualifiersOptions: IDropdownConfig[];
  totalGoalsOptions: IBettingOption<UnderOver>[];
  mostScoringTeamOptions: IDropdownConfig[];
  leastScoringTeamOptions: IDropdownConfig[];
  worstTeamOptions: IDropdownConfig[];
  groupWithMostScoredGoalsOptions: IDropdownConfig[];
  groupWithLeastScoredGoalsOptions: IDropdownConfig[];
  extraQualifiersOptions: IDropdownConfig[];
}
